import { configureStore as rtkConfigureStore } from '@reduxjs/toolkit'
import rootReducer from 'reducers'
import appMiddleware from 'middleware'
import cloneDeep from 'lodash/cloneDeep'
import { isEmbeddedReadOnly, shouldPersistState } from 'lib/embed'
import { urlSyncMiddleware, readGraphUrlState, setupPopstateListener } from 'routing'
import type { RootState } from 'types/state'


interface PersistedEntityState {
  isFetching?: boolean
  [key: string]: unknown
}

interface PersistedState {
  pendingActions?: {
    retrying?: unknown
    [key: string]: unknown
  }
  graph?: {
    drag?: unknown
    edit?: unknown
    objectDetails?: unknown
    culling?: unknown
    selected?: unknown
    creating?: unknown
    previousNeighborhoods?: unknown
    nodes?: {
      all?: Record<string, PersistedEntityState>
    }
    edges?: {
      all?: Record<string, PersistedEntityState>
    }
    [key: string]: unknown
  }
  loggingOut?: unknown
  confirm?: unknown
  users?: {
    search?: unknown
    [key: string]: unknown
  }
  permissions?: {
    newContent?: {
      expanded?: unknown
      [key: string]: unknown
    }
    [key: string]: unknown
  }
  contentGeneration?: {
    lastMode?: 'convert' | 'generate' | 'edit'
    [key: string]: unknown
  }
  [key: string]: unknown
}

const slicer = (state: PersistedState | undefined): PersistedState | undefined => {
  if (!state) {
    return state
  }
  const sliced = cloneDeep(state)

  if (sliced.pendingActions) {
    delete sliced.pendingActions.retrying
  }
  if (sliced.graph) {
    delete sliced.graph.drag
    delete sliced.graph.edit
    delete sliced.graph.objectDetails
    delete sliced.graph.culling
    delete sliced.graph.selected
    delete sliced.graph.creating
    delete sliced.graph.previousNeighborhoods
  }
  delete sliced.loggingOut
  delete sliced.confirm
  if (sliced.users) {
    delete sliced.users.search
  }
  if (sliced.permissions?.newContent) {
    delete sliced.permissions.newContent.expanded
  }
  if (sliced.contentGeneration) {
    sliced.contentGeneration = {
      lastMode: sliced.contentGeneration.lastMode,
    }
  }
  delete sliced.graphQuery

  Object.values<PersistedEntityState>(sliced.graph?.nodes?.all ?? {}).forEach((node) => {
    delete node.isFetching
  })

  Object.values<PersistedEntityState>(sliced.graph?.edges?.all ?? {}).forEach((edge) => {
    delete edge.isFetching
  })

  return sliced
}

const localStorageMiddleware = (store: any) => (next: any) => (action: any) => {
  const result = next(action)
  if (!shouldPersistState) {
    return result
  }
  try {
    const state = store.getState()
    const sliced = slicer(state)
    localStorage.setItem('redux', JSON.stringify(sliced))
  } catch {
    // ignore serialization errors
  }
  return result
}

const loadPersistedState = (): Partial<RootState> | undefined => {
  try {
    const serialized = localStorage.getItem('redux')
    if (serialized === null) return undefined
    return JSON.parse(serialized)
  } catch {
    return undefined
  }
}

const configureAppStore = () => {
  let preloadedState = shouldPersistState ? loadPersistedState() : undefined

  // Apply URL state overrides on initial load.
  // The old redux-location-state middleware would override persisted state
  // with URL params, clearing fields not present in the URL. Replicate that:
  // on /graph, the URL is the source of truth for focus, pinned, and editPerms.
  // Embeds never carry these params in their URL (SDK's focus/pin calls set them instead),
  // so skip this or it would wipe out rehydrated focus/pinned state.
  if (window.location.pathname === '/graph' && !isEmbeddedReadOnly) {
    const urlState = readGraphUrlState()
    const graphState = (preloadedState?.graph ?? {}) as Record<string, unknown>
    const focusState = (graphState.focus ?? {}) as Record<string, unknown>
    const pinnedIds = urlState.pinned
    const pinnedNodes: Record<string, string> = {}
    for (const id of pinnedIds) {
      pinnedNodes[id] = id
    }
    preloadedState = {
      ...preloadedState,
      graph: {
        ...graphState,
        focus: { ...focusState, id: urlState.focusId || '' },
        pinned: { nodes: pinnedNodes },
      },
    } as Partial<RootState>
  }

  const store = rtkConfigureStore({
    reducer: rootReducer,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: false,
        immutableCheck: false,
      }).concat(
        ...appMiddleware() as any[],
        urlSyncMiddleware,
        localStorageMiddleware,
      ),
    preloadedState,
    devTools: import.meta.env.DEV,
  })

  setupPopstateListener(store.dispatch)

  return store
}

export type AppDispatch = ReturnType<typeof configureAppStore>['dispatch']

export default configureAppStore
